import React, { useMemo, useState, useCallback } from 'react';
import { Building2, Check, Plus, Edit2, Trash2, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from 'components/context/AuthContext';
import { GoogleIcon, MetaIcon } from 'components';
import { brandsApi, usersApi } from 'api';
import toast from 'react-hot-toast';
import { GoogleAdsSection } from 'components/adsConnections/account/GoogleAdsSection';
import { MetaAdsSection } from 'components/adsConnections/account/MetaAdsSection';
import CreateBrandModal from 'components/modals/CreateBrandModal';
import EditBrandModal from 'components/modals/EditBrandModal';

const BrandsTab = ({ variant = 'full' }) => {
  const { auth, updateContextUser } = useAuth();
  const brands = useMemo(() => auth?.user?.brands ?? [], [auth?.user?.brands]);
  const activeBrandId = auth?.user?.active_brand_id;
  const [switchingId, setSwitchingId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const showBrandsList = variant === 'full';
  const allowBrandCrud = variant === 'full';

  const sortedBrands = useMemo(() => {
    if (!brands.length || !activeBrandId) return brands;
    return [...brands].sort((a, b) => {
      if (a.id === activeBrandId) return -1;
      if (b.id === activeBrandId) return 1;
      return 0;
    });
  }, [brands, activeBrandId]);

  const activeBrand = useMemo(
    () => brands.find((b) => b.id === activeBrandId),
    [brands, activeBrandId]
  );

  const handleSetActiveBrand = useCallback(
    async (brandId) => {
      if (brandId === activeBrandId) return;
      setSwitchingId(brandId);
      try {
        const res = await brandsApi.setActiveBrand(brandId);
        updateContextUser(res.data);
        toast.success('Marca activa actualizada');
        window.location.reload();
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'No se pudo cambiar la marca';
        toast.error(msg);
      } finally {
        setSwitchingId(null);
      }
    },
    [activeBrandId, updateContextUser]
  );

  const handleCreateBrand = useCallback(
    async (brandData) => {
      try {
        await brandsApi.createBrand(brandData);
        toast.success('Marca creada');
        const userResponse = await usersApi.getCurrentUser();
        updateContextUser(userResponse.data);
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Error al crear la marca';
        toast.error(msg);
        throw err;
      }
    },
    [updateContextUser]
  );

  const handleUpdateBrand = useCallback(
    async (brandData) => {
      if (!editingBrand) return;
      try {
        await brandsApi.updateBrand(editingBrand.id, brandData);
        toast.success('Marca actualizada');
        const userResponse = await usersApi.getCurrentUser();
        updateContextUser(userResponse.data);
        setEditingBrand(null);
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Error al actualizar la marca';
        toast.error(msg);
        throw err;
      }
    },
    [editingBrand, updateContextUser]
  );

  const handleDeleteBrand = useCallback(
    async (brand) => {
      if (brand.id === activeBrandId) {
        toast.error('Cambiá a otra marca activa antes de eliminar esta.');
        return;
      }
      if (brands.length <= 1) {
        toast.error('No podés eliminar la única marca de la organización.');
        return;
      }
      if (!window.confirm(`¿Eliminar la marca «${brand.name}»? Esta acción no se puede deshacer.`)) {
        return;
      }
      try {
        await brandsApi.deleteBrand(brand.id);
        toast.success('Marca eliminada');
        const userResponse = await usersApi.getCurrentUser();
        updateContextUser(userResponse.data);
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'No se pudo eliminar la marca';
        toast.error(msg);
      }
    },
    [activeBrandId, brands.length, updateContextUser]
  );

  if (!brands.length) {
    return (
      <>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-white/15 bg-white/[0.04] px-6 py-12 text-center backdrop-blur-sm">
          <Building2 className="mb-4 h-16 w-16 text-white/35" aria-hidden />
          <p className="mb-1 text-lg text-white/85">No tenés marcas cargadas</p>
          <p className="mb-6 max-w-md text-sm text-white/55">
            Creá tu primera marca para conectar cuentas publicitarias y organizar el trabajo por marca.
          </p>
          {allowBrandCrud && (
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Nueva marca
            </button>
          )}
        </div>
        {allowBrandCrud && (
          <CreateBrandModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onCreateBrand={handleCreateBrand}
          />
        )}
      </>
    );
  }

  return (
    <div className="w-full space-y-8">
      {showBrandsList && (
        <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-sm md:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">Marcas</h3>
              <p className="text-sm text-white/65">
                Agregá, editá o eliminá marcas; elegí la activa y conectá Google Ads y Meta para esa marca.
              </p>
            </div>
            {allowBrandCrud && (
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/15 md:self-auto"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Nueva marca
              </button>
            )}
          </div>

          <ul className="divide-y divide-white/10 rounded-xl border border-white/10 bg-slate-900/30">
            {sortedBrands.map((brand) => {
              const isActive = brand.id === activeBrandId;
              const busy = switchingId === brand.id;
              return (
                <li
                  key={brand.id}
                  className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span title={brand.google_ads_connected ? 'Google Ads conectado' : 'Google Ads no conectado'}>
                        <span className="relative inline-block">
                          <GoogleIcon width={18} height={18} className={brand.google_ads_connected ? 'opacity-100' : 'opacity-45'} />
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-slate-900/90 ${
                              brand.google_ads_connected ? 'bg-emerald-400' : 'bg-red-400'
                            }`}
                          />
                        </span>
                      </span>
                      <span title={brand.meta_connected ? 'Meta Ads conectado' : 'Meta Ads no conectado'}>
                        <span className="relative inline-block">
                          <MetaIcon width={18} height={18} className={brand.meta_connected ? 'opacity-100' : 'opacity-45'} />
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-slate-900/90 ${
                              brand.meta_connected ? 'bg-emerald-400' : 'bg-red-400'
                            }`}
                          />
                        </span>
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium text-white">{brand.name}</span>
                        {isActive && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/35 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-300">
                            <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                            Activa
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    {!isActive && (
                      <button
                        type="button"
                        disabled={busy || switchingId != null}
                        onClick={() => handleSetActiveBrand(brand.id)}
                        aria-label={busy ? 'Cambiando marca…' : 'Usar esta marca'}
                        title={busy ? 'Cambiando…' : 'Usar esta marca'}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <CheckCircle2 className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                    )}
                    {allowBrandCrud && (
                      <button
                        type="button"
                        onClick={() => setEditingBrand(brand)}
                        aria-label="Editar marca"
                        title="Editar marca"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white/90 transition-colors hover:bg-white/10"
                      >
                        <Edit2 className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                    {allowBrandCrud && brands.length > 1 && brand.id !== activeBrandId && (
                      <button
                        type="button"
                        onClick={() => handleDeleteBrand(brand)}
                        disabled={busy}
                        aria-label="Eliminar marca"
                        title="Eliminar marca"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 transition-colors hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {activeBrandId && activeBrand && (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white">Conexiones — {activeBrand.name}</h3>
            <p className="text-sm text-white/55">
              Administra tus conexiones con Google Ads y Meta para la marca activa.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <GoogleAdsSection />
            <MetaAdsSection />
          </div>
        </div>
      )}

      {allowBrandCrud && (
        <CreateBrandModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreateBrand={handleCreateBrand}
        />
      )}
      {allowBrandCrud && (
        <EditBrandModal
          isOpen={!!editingBrand}
          onClose={() => setEditingBrand(null)}
          brand={editingBrand}
          onUpdateBrand={handleUpdateBrand}
        />
      )}
    </div>
  );
};

export default BrandsTab;
